import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { Mail, Lock, ArrowUpRight } from "lucide-react";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("loginTitle"));
      nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="grid min-h-[calc(100vh-72px)] grid-cols-1 lg:grid-cols-2">
      {/* Editorial side */}
      <aside className="relative hidden overflow-hidden border-e lg:flex" style={{ borderColor: "hsl(var(--border))", backgroundColor: "hsl(var(--ink))" }}>
        <div className="grain absolute inset-0" />
        {/* giant watermark logo */}
        <Logo
          size={520}
          color="hsl(var(--bg) / 0.06)"
          className="absolute -end-20 -bottom-24 pointer-events-none"
        />
        <div className="relative z-10 flex h-full w-full flex-col justify-between p-12">
          <div className="flex items-center gap-2.5 text-[hsl(var(--bg))]">
            <Logo size={32} color="hsl(var(--bg))" />
            <div className="font-display text-lg">606MEDIADOCS</div>
          </div>
          <div>
            <h2 className="font-display text-6xl leading-[0.95] text-[hsl(var(--bg))]">
              <span className="font-serif text-accent">Welcome</span>
              <br />
              back to the
              <br />
              library.
            </h2>
            <p className="mt-6 max-w-sm text-sm text-[hsl(var(--bg))] opacity-70">
              {t("loginSub")}
            </p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--bg))] opacity-50">
            §01 · sign in
          </div>
        </div>
      </aside>

      {/* Form side */}
      <div className="flex items-center justify-center px-6 py-16 sm:px-12">
        <div className="w-full max-w-md reveal" data-testid="login-card">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-fg">
            01 · {t("signIn")}
          </div>
          <h1 className="font-display mt-3 text-5xl leading-[0.95]">
            {t("loginTitle").split(" ")[0]}{" "}
            <span className="font-serif text-accent">
              {t("loginTitle").split(" ").slice(1).join(" ") || "in"}
            </span>
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <Field label={t("email")} icon={Mail}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-base ps-10"
                data-testid="login-email"
              />
            </Field>
            <Field
              label={t("password")}
              icon={Lock}
              right={
                <Link to="/forgot-password" className="text-xs font-semibold text-accent hover:underline" data-testid="login-forgot-link">
                  {t("forgotPassword")}
                </Link>
              }
            >
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base ps-10"
                data-testid="login-password"
              />
            </Field>

            {err && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300" data-testid="login-error">{err}</div>}

            <button type="submit" disabled={loading} className="btn btn-primary w-full !py-3.5" data-testid="login-submit">
              {loading ? "…" : t("signIn")}
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </form>

          <p className="mt-8 text-sm text-muted-fg">
            {t("needAccount")}{" "}
            <Link to="/register" className="font-semibold text-ink underline decoration-accent decoration-2 underline-offset-4 hover:text-accent" data-testid="login-to-register">
              {t("createAccount")}
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}

function Field({ label, icon: Icon, children, right }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-fg">{label}</label>
        {right}
      </div>
      <div className="relative mt-1.5">
        {Icon && <Icon className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-fg" />}
        {children}
      </div>
    </div>
  );
}
